import { signal } from "@preact/signals";
import { useState } from "preact/hooks";
import type { ComponentChildren } from "preact";

import { addressList, displayName, fileSize, fullDate, hueFor, initials } from "../lib/format";
import { bringsOwnColours, inlineCidImages, messageHeader, renderHtmlBody, renderTextBody } from "../lib/html";
import type { HeaderFields } from "../lib/html";
import * as jmap from "../lib/jmap";
import * as reminders from "../lib/reminders";
import * as retention from "../lib/retention";
import { isDark, loadRemoteImages } from "../lib/settings";
import * as store from "../lib/store";
import * as tabs from "../lib/tabs";
import type { BodyPart, Email, EmailAddress, Envelope, Session } from "../lib/types";
import { ActionBar } from "./Actions";
import { multiWindow, saveFiles } from "@transport";
import { openForward, openReply, openReplyAll } from "./Compose";
import * as Icon from "./Icons";

/**
 * Messages the reader has chosen to load remote content for, this session.
 *
 * Not persisted. "Show images once" is a decision about one message in front
 * of you; remembering it forever is a different decision, and the global
 * setting in Settings is where that one lives.
 */
const unblocked = signal<Set<string>>(new Set());

/**
 * Messages the reader has been asked to show in their own colours, this
 * session, while the app is dark.
 *
 * The dark app rewrites a coloured message's palette for a dark canvas (see
 * `darkenColours` in `lib/darken.ts`); this is the way out for the one that
 * looks wrong -- a photo on a card whose white is baked into the pixels, a
 * chart whose meaning was its colours. Session-only for the same reason as
 * `unblocked`: it is a decision about one message in front of you.
 */
const onWhite = signal<Set<string>>(new Set());

/**
 * `inTab` says this reader *is* a message tab rather than the reading pane
 * beside a list. It changes one control: see the Close button at the foot.
 *
 * Passed in rather than worked out here, because the two facts that would have
 * to be combined to answer it are not both available in this component. A
 * detached message window restores the strip's tabs at startup like any other
 * window does — nothing is drawing them, but `tabs.current` there can still be
 * a message tab, so asking it directly would put a tab-closing button in a
 * window that has no tabs.
 */
/**
 * Print the open message.
 *
 * # Why the frame prints itself
 *
 * The message body is a sandboxed, opaque-origin iframe. Calling
 * `window.print()` on the *parent* prints the page around it, and WebKit prints
 * a nested cross-origin frame as a scrolled box -- clipped to what is on screen,
 * scrollbars and all, not the whole message (a Chromium `printToPDF` paginated
 * it and hid that, which is why this shipped looking fine and was not). So the
 * frame is asked to print *itself*: an iframe's own `window.print()` renders its
 * whole document, paginated, with no outer chrome and no scrollbars -- the
 * standard "print in an iframe" technique.
 *
 * That needs `allow-modals` on the sandbox, which is safe here for a reason
 * specific to this frame: its CSP admits exactly one script by hash
 * (`FIT_SCRIPT`), so a hostile message cannot run code at all, let alone call
 * `print()` or `alert()` -- `allow-modals` grants a capability nothing but our
 * own pinned script can reach. `FIT_SCRIPT` listens for the message this posts
 * and calls `print()`; the frame's own `@media print` keeps the paper white.
 */
function printMessage(): void {
  const frame = document.querySelector<HTMLIFrameElement>("iframe.body-frame");
  frame?.contentWindow?.postMessage("hm-print", "*");
}

/**
 * Fetch every listed attachment and hand them to the transport to save.
 *
 * The desktop asks for a folder once and writes them all there; the browser
 * downloads each -- that split lives in `saveFiles`, not here. Fetched the same
 * authenticated way a single save fetches; a failure toasts rather than throws,
 * so one bad blob does not sink the rest of the batch's error reporting.
 */
async function downloadAll(
  session: Session,
  accountId: string,
  parts: BodyPart[],
): Promise<void> {
  try {
    const files = await Promise.all(
      parts.map(async (p) => ({
        name: p.name ?? "attachment",
        blob: await jmap.fetchAttachment(session, accountId, {
          blobId: p.blobId as string,
          name: p.name ?? "attachment",
          type: p.type ?? "application/octet-stream",
        }),
      })),
    );
    await saveFiles(files);
  } catch (e) {
    store.toast(e instanceof Error ? e.message : String(e), "error");
  }
}

export function Reader({ inTab = false }: { inTab?: boolean }) {
  const row = store.openEmail.value;

  if (!row) {
    /*
     * Asked for a specific message and the row is not here yet.
     *
     * Opening from the list paints the header from the row immediately, so this
     * only happens when the message was named rather than picked: a link
     * followed in, a notification tapped, a reload of an address with one open.
     * "No message selected" would be the wrong sentence at somebody who has
     * just selected one, and on a phone it is the whole screen.
     */
    const named = store.openEmailId.value !== null;
    const failed = store.openError.value;
    if (named && failed) {
      return (
        <section class="col reader-col" data-pane-name="reader" tabIndex={-1} aria-label="Message">
          <div class="empty">
            <Icon.Inbox />
            <h2>That message could not be opened</h2>
            <p>{failed}</p>
          </div>
        </section>
      );
    }
    if (named) {
      return (
        <section class="col reader-col" data-pane-name="reader" tabIndex={-1} aria-label="Message">
          {/*
            Deliberately wordless. The wait is one round trip, and a sentence
            that appears and is gone before it can be read is noise -- while a
            heading naming the wrong outcome, which is what any guess here would
            be, is worse than nothing.
          */}
          <div class="empty" aria-busy="true" />
        </section>
      );
    }
    return (
      <section class="col reader-col" data-pane-name="reader" tabIndex={-1} aria-label="Message">
        <div class="empty">
          <Icon.Inbox />
          <h2>No message selected</h2>
          <p>
            Pick something from the list, or press <kbd>j</kbd> and <kbd>k</kbd> to walk it.
          </p>
        </div>
      </section>
    );
  }

  const { email, accountId } = row;
  const key = `${accountId}:${email.id}`;
  // Only where there is a choice to have got wrong. One account is not an
  // ambiguity worth a line of the header.
  const account =
    store.accounts.value.length > 1
      ? store.accounts.value.find((a) => a.id === accountId)
      : undefined;
  const allowRemote = loadRemoteImages.value || unblocked.value.has(key);
  const darkenMail = isDark.value && !onWhite.value.has(key);
  // The embedded-image map for this message. Empty until its parts are
  // fetched; reading it here is what re-renders the body once they arrive.
  const inline = store.session.value
    ? inlineImagesFor(store.session.value, accountId, email)
    : EMPTY_INLINE;
  const body = renderBody(email, isDark.value, allowRemote, account?.name, inline, darkenMail);

  // Inline images render in the body, so they are not also listed as
  // attachments -- but only the ones that actually resolved: a part that could
  // not be fetched keeps its chip, so it is still reachable. A non-inline
  // attachment, or one with no `cid`, is never hidden.
  const attachments =
    email.attachments?.filter((a) => a.blobId && !(a.cid && inline.has(a.cid))) ?? [];

  return (
    <section class="col reader-col" data-pane-name="reader" tabIndex={-1} aria-label="Message">
      <div class="reader">
        <div class="reader-head">
          <div class="reader-title">
            {/*
              Rendered on every width and shown only where the reader is the
              whole window. It says "message list" rather than carrying a bare
              chevron because on a phone this is the primary way out, and the
              only other one -- the system back button -- has no label at all.
            */}
            <button
              class="icon-btn back-btn"
              aria-label="Back to the message list"
              onClick={store.closeEmail}
            >
              <Icon.Back />
            </button>
            <h1 class="reader-subject">{email.subject || "(no subject)"}</h1>
          </div>
          <MessageHeader email={email} account={account?.name} />
        </div>


        {/*
          Above the message rather than after it.

          It used to be the last element in the column, which is where the
          phone's floating toolbar now sits -- the strip had to carry 84px of
          blank padding underneath it just so the dock had something to float
          over, and the dock still landed on it. Moving it here removes the
          collision instead of negotiating with it.

          Under the header rather than in it, and above the body rather than
          below: what is attached is part of what arrived, like the sender and
          the date, and a message whose point is the PDF should not need the
          body read first to find out. Moved in the markup rather than by
          `order`, so that the reading order a screen reader gets is the order
          on screen -- and so that both targets agree, since the dock this
          dodges exists on only one of them.
        */}
        {attachments.length > 0 && (
          <div class="attachments">
            {/*
              Only when there is more than one to gather: for a single
              attachment its own pill already is the download, and a second
              control saying the same thing is clutter.
            */}
            {attachments.length > 1 && store.session.value && (
              <button
                class="btn ghost download-all"
                onClick={() => void downloadAll(store.session.value!, accountId, attachments)}
              >
                <Icon.KeepOffline />
                <span>Download all ({attachments.length})</span>
              </button>
            )}
            {attachments.map((part) => (
              <Attachment key={part.blobId} accountId={accountId} emailId={email.id} part={part} />
            ))}
          </div>
        )}
        {store.openError.value && (
          <div class="banner error">
            <Icon.Warning />
            <span>{store.openError.value}</span>
          </div>
        )}

        {/*
          The blocked-images notice used to be a banner here, and it is now a
          button on `.reader-bar` instead. See `RemoteImagesButton` below.
        */}

        {store.openLoading.value && !body.ready ? (
          <div class="empty">
            <span class="spinner" />
          </div>
        ) : (
          /*
            The frame sits in a box that scrolls, and the box is what bounds it.

            Mail is full of 640px tables and 900px images, and on a phone the
            question is only ever where the sideways scroll happens: inside the
            message, or under the whole app. Every browser but Safari on iOS
            answers "inside the frame" by itself, because the frame is a
            viewport and it scrolls. iOS instead grows the frame to whatever its
            content measures and hands the overflow to the page -- which is how
            a newsletter ends up dragging the sidebar off screen. The wrapper
            takes that overflow: the frame may be as wide as it likes, and the
            scrolling stays in this box.

            The frame is the vertical scroller, and the header above it stays
            put: it is the app's own markup (`MessageHeader`), not part of the
            document, so the mail's stylesheet cannot reach it. From 2026-08-17
            it was rendered into the document instead, to scroll away with the
            mail, and every new template found a new way to knock it about --
            see the module note in `lib/html.ts` for how that ended. Sizing the
            frame to its content so this box could scroll both together was
            tried the same day; it needed `allow-same-origin` to measure, and
            was reverted for the simpler and safer arrangement.

            None of this touches the sandbox or the CSP; see `lib/html.ts`.
          */
          <div class="body-scroll">
            <iframe
              /*
                A new element whenever the content changes, rather than the
                same one re-pointed.

                Writing `srcdoc` on a live frame is a *navigation*, and an
                iframe navigation adds an entry to the joint session history --
                so pressing "Show images" left a spare entry behind and the
                next back press spent itself undoing that instead of closing
                the message. It looked exactly like a dead back button: nothing
                moved, and it took two presses to leave. The same was true of
                switching theme with a message open.

                Changing the key makes Preact drop the frame and insert a fresh
                one, and a newly inserted iframe's first load replaces its entry
                rather than pushing one.

                Keyed on the document itself rather than on a list of the things
                believed to produce it, because that list was wrong once
                already: it named the message, the theme and the remote-image
                choice, and missed `ready`, so a body arriving after the preview
                changed `srcdoc` without changing the key. A string compare per
                render is cheaper than keeping a list like that honest.
              */
              key={body.srcdoc}
              class="body-frame"
              title="Message body"
              /*
                `allow-scripts`, and still no `allow-same-origin`. The second
                omission is the one carrying the weight: without it the frame
                is an opaque origin, so script inside it cannot reach this
                document, this origin's `localStorage`, or the device token,
                whatever else it manages to run.

                And what may run is one script -- the CSP inside the document
                names a single SHA-256, so the message's own scripts, external
                or inline, are refused, as are its `onclick` attributes, since
                a hash source does not imply `unsafe-hashes`. See `FIT_SCRIPT`
                in `lib/html.ts` for what the one script does and why measuring
                the message needs to happen in here at all.

                Granting both flags together would undo the sandbox entirely --
                a frame that is same-origin *and* scripted can reach straight
                back into the app. They must never both appear on this element.
                `html.test.ts` asserts that, because it is the kind of thing a
                later edit adds while trying to fix something else.
              */
              sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-modals"
              referrerpolicy="no-referrer"
              srcdoc={body.srcdoc}
            />
          </div>
        )}


        {/*
          The toolbar, last in the markup and moved above the subject by the
          stylesheet on anything wider than a phone.

          It used to sit above the subject in the markup too, and the reason it
          was there still holds on a desktop: a float beside a subject that is
          allowed to wrap moves every time the subject gets longer. But on a
          phone, eight controls at a touchable size do not fit on one line
          under a back button, and wrapping them costs a fifth of the screen
          before a word of the message is visible. At the foot they are one row,
          within reach of a thumb, and the message starts at the top of the pane
          where it belongs. `order` does the moving, so the reading order stays
          the same for a screen reader either way: subject, sender, message,
          then what you can do about it.
        */}
        {/*
          Draggable too, because at desktop widths this bar is `order: -1` and
          is therefore the top-right of the window -- the same band as the
          header rows beside it, and the part of it somebody is most likely to
          reach for. Every child here is a `<button>` except the spacer, and
          Tauri's script treats buttons as blocking, so the only thing that
          drags is the empty stretch between the actions and Close. Which is the
          right answer: that stretch is the one place in this row with nothing
          to click, and outside a message tab it now runs all the way to the
          window's edge.

          One consequence to know rather than discover: below 48rem this bar is
          `order: 9` and sits at the *bottom* of the window, so there it drags
          the window from a bottom toolbar. Unusual, not harmful, and the south
          resize grip still wins the last four pixels because it is fixed and
          higher in the stack.
        */}
        <div class="reader-bar" data-tauri-drag-region="deep">
          <button class="icon-btn" title="Reply" aria-label="Reply" onClick={() => openReply(row)}>
            <Icon.Reply />
          </button>
          {/*
            Beside Reply rather than in the menu, because deciding between the
            two is the whole of the decision -- replying to a thread and
            replying to its sender are different messages, and the choice is
            made at the moment of reply, not looked up. It has had a shortcut
            (`A`, see `lib/shortcuts.ts`) since long before it had a button;
            this is the pointer's way to the same thing.
          */}
          <button
            class="icon-btn"
            title="Reply to everyone"
            aria-label="Reply to everyone"
            onClick={() => openReplyAll(row)}
          >
            <Icon.ReplyAll />
          </button>
          {/*
            Third in the row, and late to it. Forward has had a shortcut (`F`)
            and a fully built composer path for as long as the other two, and
            no button -- which nobody at a keyboard noticed, and which the
            Android port noticed at once, because a phone has no `F` to press.
            Its PARITY.md recorded "upstream it is keyboard-only" as the reason
            it grew a button of its own; this is upstream catching up.
          */}
          <button class="icon-btn" title="Forward" aria-label="Forward" onClick={() => openForward(row)}>
            <Icon.Forward />
          </button>
          <span class="bar-rule wide-only" aria-hidden="true" />
          <ActionBar
            rows={[row]}
            demote
            // The two controls that are about this machine rather than about
            // the mail -- see each one's own note -- ride at the end of the
            // bar, behind a rule and ahead of the menu, so that the menu is
            // the last thing on it. They used to come after it, which left
            // an overflow menu with controls to its right.
            trailing={
              <>
                {/*
                  Archive, Trash and Flag are statements about the mail and
                  reach every device the account is on; this is a statement
                  about *this* computer's disk, and it changes nothing anybody
                  else can see. Absent entirely in the browser build, where
                  there is no local store -- see `lib/retention.ts`. A control
                  that could not keep its promise is worse than no control,
                  because a saved message is relied on in a tunnel.
                */}
                <ReminderChip row={row} />
                <KeepOfflineButton row={row} />
                {/*
                  Present only on a message that is actually missing
                  something, which is what lets it be a bare icon: it is never
                  one of a row of controls competing to be read, it is the odd
                  one out on the few messages that have it.
                */}
                {body.hasRemoteContent && !allowRemote && (
                  <RemoteImagesButton
                    onShow={() => (unblocked.value = new Set(unblocked.value).add(key))}
                  />
                )}
                {/*
                  Only when there is a palette to argue about: the app is dark
                  and this message brought colours of its own, so it is being
                  shown rewritten. A plain message takes the theme with nothing
                  to switch, and a light app shows every message as sent.
                */}
                {isDark.value && body.darkenable && (
                  <OwnColoursButton
                    own={onWhite.value.has(key)}
                    onToggle={() => {
                      const next = new Set(onWhite.value);
                      if (next.has(key)) next.delete(key);
                      else next.add(key);
                      onWhite.value = next;
                    }}
                  />
                )}
                {/*
                  Print, wide-only: a printer is a desktop thing, and the bar
                  is tight on a phone. `printMessage` sizes the body frame to
                  its whole content first -- see there for why the plain
                  `window.print()` would clip a long message to one screen.
                */}
                <button
                  class="icon-btn wide-only"
                  title="Print this message"
                  aria-label="Print this message"
                  onClick={printMessage}
                >
                  <Icon.Printer />
                </button>
              </>
            }
          />
          <span class="spacer" />
          {/*
            Only in a message tab, and there it closes the tab.

            It used to be here in every layout, as a second way out of the
            reading pane beside the list. Amit's call, 2026-08-16: Escape
            already does that, and a button for a keystroke that works is a
            button in the way of the mail.

            In a tab there is no such keystroke -- Escape deliberately does
            nothing in one, so that a view cannot be taken away by a key pressed
            by accident (see `escape` in `app.tsx`). Closing the tab is also the
            only thing this button could honestly mean here: `store.closeEmail`
            is what it used to call, and in a tab that is a no-op, because
            `lib/tabview.ts` re-asserts the tab's message the instant anything
            clears it. A button that flickers and changes nothing is worse than
            no button.

            Absent rather than disabled where it does not apply, which is the
            rule this codebase already follows for the offline pane and the
            titlebar setting. It stays `wide-only` on top of that: on a phone
            the back button is two inches away and does the same job.
          */}
          {inTab && (
            <button
              class="icon-btn wide-only"
              title="Close this tab"
              aria-label="Close this tab"
              onClick={() => tabs.close(tabs.current.value.id)}
            >
              <Icon.Close />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function Attachment({
  accountId,
  emailId,
  part,
}: {
  accountId: string;
  emailId: string;
  part: BodyPart;
}) {
  const session = store.session.value;
  return (
    <button
      class="attachment"
      title={`${part.name ?? "attachment"} — ${part.type}`}
      /*
        Opens it where there are tabs, and saves it where there are not.
        Viewing is the commoner intent by a wide margin -- most attachments are
        looked at once and never kept -- but a browser has nowhere to put a
        viewer that is not in the way, and the save dialog is what that build
        has always done. The viewer's own bar carries Save, so the desktop
        loses nothing by making the click the cheaper thing.
      */
      onClick={() => {
        if (!session || !part.blobId) return;
        const ref = {
          blobId: part.blobId,
          name: part.name ?? "attachment",
          type: part.type ?? "application/octet-stream",
        };
        if (multiWindow) {
          tabs.open({ account: accountId, email: emailId, part: ref });
          return;
        }
        void jmap
          .saveAttachment(session, accountId, ref)
          .catch((e: unknown) => store.toast(e instanceof Error ? e.message : String(e), "error"));
      }}
    >
      <Icon.Paperclip />
      <span class="name">{part.name ?? "attachment"}</span>
      <span class="size">{fileSize(part.size)}</span>
    </button>
  );
}

interface Body {
  srcdoc: string;
  hasRemoteContent: boolean;
  /** False while only the list row is loaded and the bodies have not arrived. */
  ready: boolean;
  /** True for an HTML body that brought its own colours -- one the dark app rewrites. */
  darkenable: boolean;
}

/**
 * Pick a body part and render it.
 *
 * HTML wins when there is real HTML. That is the opposite of the privacy-first
 * instinct, but the text alternative in a modern multipart message is routinely
 * a stub that says "view this in a browser", and the remote content that makes
 * HTML risky is already blocked a layer down.
 *
 * The subtlety is that `htmlBody` is not "the HTML parts". RFC 8621 defines it
 * as the parts to display *as* the HTML view, which for a message that has no
 * HTML at all is the `text/plain` part — so `htmlBody[0]` is very often plain
 * text. Handing that to the HTML renderer collapses every newline and loses
 * every quote marker, which is exactly what a plain-text mailing list looks
 * like when a client gets this wrong. The part's own `type` is the thing to
 * ask.
 */
/**
 * Embedded (`cid:`) images for the message on screen, resolved to `data:` URIs.
 *
 * # Why a signal and a cache rather than a hook
 *
 * The reader is signal-driven and returns early in several branches, so a
 * `useEffect` would be a conditional hook. This is the same shape the rest of
 * the file uses: a module signal holding the answer for one message, filled by
 * an async load that a render starts and reads back when it lands. One
 * message's worth at a time is enough -- the reader shows one.
 *
 * # The message grows under it
 *
 * A row is opened as a list envelope -- no body, no parts -- and the full
 * message arrives a round trip later. So the decision to load, and the cache,
 * are keyed on there being inline parts to fetch: while the message is still a
 * stub, [`inlineCandidates`] is empty and nothing is cached, so the render that
 * finally has the parts is the one that starts the fetch. Caching an empty
 * answer from the stub was the first version's bug -- the image never loaded
 * because the key already looked done.
 *
 * The bytes come from the same place the attachment viewer's do
 * (`fetchAttachment` -> the local blob store on desktop, the download endpoint
 * in the browser), so an embedded image costs what opening it as an attachment
 * would, and nothing when the message has none.
 */
interface InlineState {
  key: string;
  images: Map<string, string>;
}

const EMPTY_INLINE: Map<string, string> = new Map();
const inlineImages = signal<InlineState>({ key: "", images: EMPTY_INLINE });
/** Keys a load is already running for, so a render does not start a second. */
const inlineInFlight = new Set<string>();

/** The inline image parts this message's HTML actually points at. */
function inlineCandidates(email: Email): BodyPart[] {
  const htmlPart = email.htmlBody?.find((p) => p.type === "text/html");
  const html = htmlPart?.partId ? email.bodyValues?.[htmlPart.partId]?.value : undefined;
  if (html === undefined) return [];
  return (email.attachments ?? []).filter(
    (p) => p.cid !== null && p.blobId !== null && html.includes(`cid:${p.cid}`),
  );
}

function inlineImagesFor(
  session: Session,
  accountId: string,
  email: Email,
): Map<string, string> {
  const key = `${accountId}:${email.id}`;
  if (inlineImages.value.key === key) return inlineImages.value.images;
  const wanted = inlineCandidates(email);
  // Nothing to embed yet -- either this message has no inline images, or its
  // body has not arrived. Return empty *without* caching, so the render that
  // does have the parts gets to start the load.
  if (wanted.length === 0) return EMPTY_INLINE;
  if (!inlineInFlight.has(key)) {
    inlineInFlight.add(key);
    // Off the render path: starting a fetch is a side effect, and a render
    // must not be the place it happens.
    queueMicrotask(() => void loadInline(session, accountId, key, wanted));
  }
  return EMPTY_INLINE;
}

async function loadInline(
  session: Session,
  accountId: string,
  key: string,
  parts: BodyPart[],
): Promise<void> {
  try {
    const images = new Map<string, string>();
    await Promise.all(
      parts.map(async (p) => {
        try {
          const blob = await jmap.fetchAttachment(session, accountId, {
            blobId: p.blobId as string,
            name: p.name ?? "inline",
            type: p.type,
          });
          images.set(p.cid as string, await dataUri(blob));
        } catch {
          // Left unresolved -- a broken image and a kept chip, never a throw
          // that takes the whole body down with it.
        }
      }),
    );
    inlineImages.value = { key, images };
  } finally {
    inlineInFlight.delete(key);
  }
}

/** A blob as a `data:` URI, its media type taken from the blob itself. */
function dataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("could not read the inline image"));
    reader.readAsDataURL(blob);
  });
}

function renderBody(
  email: Email,
  dark: boolean,
  allowRemote: boolean,
  account: string | undefined,
  inline: Map<string, string>,
  darkenMail: boolean,
): Body {
  const value = (part: BodyPart | undefined): string | undefined => {
    if (!part?.partId) return undefined;
    return email.bodyValues?.[part.partId]?.value;
  };

  /*
   * The frame's copy of the header, which it shows only when it prints. The
   * one on screen is `MessageHeader`, in this document; both are built from
   * `headerFacts` so paper and screen cannot disagree about who sent what.
   *
   * Every branch below gets the same header, including the two that render a
   * preview, so that a message printed while still arriving has its sender.
   */
  const header = messageHeader(headerFacts(email, account));

  const htmlPart = email.htmlBody?.find((p) => p.type === "text/html");
  const html = value(htmlPart);
  if (html !== undefined) {
    // Embedded images resolved to `data:` URIs before framing -- see
    // `inlineCidImages`. The map is this message's, fetched by `loadInline`;
    // an unresolved `cid:` (offline, or a part that failed) is left a broken
    // image, exactly as before this existed.
    return {
      ...renderHtmlBody(inlineCidImages(html, inline), dark, allowRemote, header, darkenMail),
      ready: true,
      darkenable: bringsOwnColours(html),
    };
  }

  const textPart = email.textBody?.[0] ?? email.htmlBody?.[0];
  const text = value(textPart);
  if (text !== undefined) return { ...renderTextBody(text, dark, header), ready: true, darkenable: false };

  if (email.preview) return { ...renderTextBody(email.preview, dark, header), ready: false, darkenable: false };
  return { ...renderTextBody("", dark, header), ready: false, darkenable: false };
}

/**
 * The header's facts, in one place, because two things render them.
 *
 * `MessageHeader` draws them on screen and `messageHeader` (in `lib/html.ts`)
 * writes them into the frame for the printout. Derived once so that the two
 * cannot drift -- the same name, the same truncated recipient list, the same
 * decision about whether the address is worth a line of its own.
 */
function headerFacts(email: Email, account: string | undefined): HeaderFields {
  const sender = email.from?.[0];
  return {
    initials: initials(sender),
    hue: hueFor(sender?.email ?? "?"),
    name: displayName(sender) || "(unknown sender)",
    // Only when it adds something. A name that *is* the address would print it
    // twice.
    ...(sender?.name ? { address: sender.email } : {}),
    date: fullDate(email.receivedAt),
    dateExact: email.receivedAt,
    to: addressList(email.to, 4) || "(undisclosed)",
    ...(email.cc && email.cc.length > 0 ? { cc: addressList(email.cc, 3) } : {}),
    ...(account ? { account } : {}),
  };
}

/**
 * Who sent it, when, and to whom -- in the app's document, above the frame.
 *
 * Above rather than inside the message on purpose: this markup shares nothing
 * with the mail, so no `body { margin: 0 }` reset, centred-text rule or
 * `!important` of the sender's can move a pixel of it. That is the whole reason
 * it is here and not in the frame; the history is on `lib/html.ts`. It costs
 * the header its scroll-away, which is why it is kept to three short rows.
 *
 * The recipients are a definition list for the reason the framed version was:
 * "To", "Cc" and the account are label/value pairs, the labels are what let the
 * addresses line up in one column, and a screen reader gets pairs rather than
 * a run-on line. Same classes as the header this pane had before 2026-08-17,
 * so the stylesheet's `.reader-from` rules are back in use rather than
 * duplicated under a new name.
 */
function MessageHeader({ email, account }: { email: Email; account: string | undefined }) {
  const f = headerFacts(email, account);
  const to = email.to ?? [];
  const cc = email.cc ?? [];
  const rows: [string, ComponentChildren][] = [
    ["To", to.length > 0 ? <Recipients list={to} max={4} /> : f.to],
    ["Cc", cc.length > 0 ? <Recipients list={cc} max={3} /> : undefined],
    ["Account", f.account],
  ];
  return (
    <div class="reader-from">
      <span class="avatar" style={{ background: `hsl(${f.hue} 55% 45%)` }} aria-hidden="true">
        {f.initials}
      </span>
      <div class="reader-meta">
        <div class="line">
          <strong class="sender">{f.name}</strong>
          <span class="addr sent-at" title={f.dateExact}>
            {f.date}
          </span>
        </div>
        {f.address && <div class="addr from-addr">{f.address}</div>}
        <dl class="rows">
          {rows
            .filter((r) => Boolean(r[1]))
            .map(([label, value]) => (
              <>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </>
            ))}
        </dl>
      </div>
    </div>
  );
}

/**
 * A recipient list that fits on a line, with everyone's address a click away.
 *
 * The first few names, then "+N" as a button rather than as text: a message
 * to a dozen people otherwise had no way to say who the other eight were.
 * Each name carries its address as a tooltip, since the name alone does not
 * say which Sarah -- and since a touch screen has no hover, clicking a name
 * (or "+N") opens the list as one recipient per line with their addresses,
 * Gmail's details view.
 */
function Recipients({ list, max }: { list: EmailAddress[]; max: number }) {
  const [open, setOpen] = useState(false);
  if (open) {
    return (
      <>
        {list.map((a, i) => (
          <span key={`${i}:${a.email}`} class="recipient-full">
            {a.name && a.name.trim() !== a.email ? `${a.name.trim()} <${a.email}>` : a.email}
          </span>
        ))}
        <button type="button" class="recipients-more" onClick={() => setOpen(false)}>
          Less
        </button>
      </>
    );
  }
  const shown = list.slice(0, max);
  const rest = list.length - shown.length;
  return (
    <>
      {shown.map((a, i) => (
        <span key={`${i}:${a.email}`}>
          {i > 0 && ", "}
          <button
            type="button"
            class="recipient"
            title={a.email}
            onClick={() => setOpen(true)}
          >
            {displayName(a)}
          </button>
        </span>
      ))}
      {rest > 0 && (
        <>
          {" "}
          <button
            type="button"
            class="recipients-more"
            aria-label={`Show all ${list.length} recipients`}
            onClick={() => setOpen(true)}
          >
            +{rest}
          </button>
        </>
      )}
    </>
  );
}

export type { Envelope };

/**
 * Keep this message readable with no network, or stop.
 *
 * Not optimistic, unlike every other control in the reading bar, and the
 * difference is deliberate: pinning downloads a body, so it takes a moment and
 * can fail. Saying "kept" before the bytes have landed would be the exact false
 * promise this feature exists to avoid, so the button reports that it is
 * working and only changes state when Rust says the body is here.
 */
/**
 * The reminder on this message, when it has one: its time, and a way to say
 * it is dealt with. Setting and moving live in the menu (Remind me); this is
 * the reminder's own face on the message, and the one control a person
 * reaches for when the notification brought them here.
 */
function ReminderChip({ row }: { row: Envelope }) {
  const r = reminders.forMessage(row.accountId, row.email.id);
  if (!r) return null;
  const label = reminders.describe(r);
  return (
    <span class={r.state === "overdue" ? "reminder-chip overdue" : "reminder-chip"}>
      <Icon.Bell class="icon" />
      <span title={`Reminder ${label}`}>{label}</span>
      <button
        class="icon-btn"
        title="Complete reminder"
        aria-label="Complete reminder"
        onClick={() =>
          void reminders
            .complete(r.id)
            .then(() => store.refreshReminders())
            .catch((e: unknown) => store.toast(e instanceof Error ? e.message : String(e), "error"))
        }
      >
        <Icon.Check />
      </button>
    </span>
  );
}

function KeepOfflineButton({ row }: { row: Envelope }) {
  if (!retention.available) return null;

  const key = store.keyOf(row);
  const kept = retention.isPinned(key);
  const busy = retention.isWorking(key);
  const label = busy
    ? kept
      ? "Removing…"
      : "Saving…"
    : kept
      ? "Saved — remove"
      : "Save";

  return (
    <button
      // No state classes: the two ARIA attributes below drive the styling as
      // well, so there is one source of truth for "kept" rather than a class
      // and an attribute that can drift apart. `keep` names the control, not a
      // state -- it is what lets the stylesheet give this one its ring while
      // the verbs beside it keep theirs. See `.icon-btn.keep` in the stylesheet.
      class="icon-btn keep"
      title={label}
      aria-label={label}
      aria-pressed={kept}
      aria-busy={busy}
      onClick={() => void retention.setPinned(row.accountId, row.email.id, !kept)}
    >
      <Icon.KeepOffline />
    </button>
  );
}

/**
 * Load the images this message wanted to fetch.
 *
 * **This was a banner and is now a button, and the reason is height.** The
 * notice lived in a strip between the header and the body, and `.banner` is a
 * nowrap flex row -- so its sentence shrank to min-content and wrapped inside
 * the row rather than pushing anything sideways. On a 312px phone it stood six
 * lines tall above a message that had about fourteen to give. Shortening the
 * text bought two of those back; moving it here buys all of them, because
 * `.reader-bar` is a row that exists on every message already and this costs
 * one icon's width in it.
 *
 * # What that trades away, and why it is the right trade
 *
 * A banner announces itself and a toolbar button does not, so the *reason* the
 * default exists -- fetching a remote image tells the sender their address is
 * live and when it was opened -- can no longer be a sentence in front of
 * somebody who has not asked for it. It is on the `title` and the `aria-label`,
 * where a pointer and a screen reader both find it, and in full on the Privacy
 * page. On a touch screen it is effectively out of reach.
 *
 * The judgement is that the sentence had already stopped doing that job. It sat
 * on every message with a tracking pixel in it -- which is most marketing mail
 * -- and a warning that appears constantly is read once and then not again.
 * What has to survive is the *offer*, so that a newsletter which is genuinely
 * unreadable without its images can be made readable, one message at a time,
 * without turning images on for everything. That is exactly what this is.
 *
 * Tinted `--warn` rather than drawn like its neighbours, because it is not one
 * of them: Reply and Archive are always there and this appears only when
 * something is being withheld. The colour is what replaces the banner's job of
 * catching an eye that was not looking for it.
 */
/**
 * Switch one message between the dark app's rewritten palette and the colours
 * it was sent with. `aria-pressed` while it is showing its own, since that is
 * the departure from how the dark app shows everything else.
 */
function OwnColoursButton({ own, onToggle }: { own: boolean; onToggle: () => void }) {
  const label = own
    ? "Darken this message again"
    : "Show this message in its own colours, as the sender designed it";
  return (
    <button class="icon-btn own-colours" title={label} aria-label={label} aria-pressed={own} onClick={onToggle}>
      {own ? <Icon.Moon /> : <Icon.Sun />}
    </button>
  );
}

function RemoteImagesButton({ onShow }: { onShow: () => void }) {
  const label = "Show blocked images — loading them tells the sender you opened this";
  return (
    <button class="icon-btn blocked-images" title={label} aria-label={label} onClick={onShow}>
      <Icon.Image />
    </button>
  );
}
